import { create } from 'zustand';
import { api, toQueryString } from '@/lib/api';
import type { ApiResponse, AutonomyRule } from '@nexus/shared/types';
import { toast } from './toastStore';

interface AutonomyState {
  rules: AutonomyRule[];
  isLoading: boolean;
  error: string | null;
  fetch: (module?: string) => Promise<void>;
  upsert: (data: Record<string, unknown>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useAutonomyStore = create<AutonomyState>((set, get) => ({
  rules: [],
  isLoading: false,
  error: null,

  fetch: async (module) => {
    set({ isLoading: true, error: null });
    try {
      const params = module ? { module } : undefined;
      const res = await api.get<ApiResponse<AutonomyRule[]>>(`/api/autonomy/rules${toQueryString(params)}`);
      set({ rules: res.data ?? [], isLoading: false });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  upsert: async (data) => {
    try {
      await api.put('/api/autonomy/rules', data);
      get().fetch();
      toast.success('Autonomy rule updated');
    } catch (err) {
      toast.error('Failed to update autonomy rule', (err as Error).message);
      throw err;
    }
  },

  remove: async (id) => {
    try {
      await api.del(`/api/autonomy/rules/${id}`);
      set((s) => ({ rules: s.rules.filter((r) => r.id !== id) }));
      toast.success('Autonomy rule deleted');
    } catch (err) {
      toast.error('Failed to delete autonomy rule', (err as Error).message);
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
